import { IntegrationsTabSharedContext } from "pages/subscription-settings/integrations-tab-new/integrations-tab-context";
import {
  useCallback,
  useContext,
  useEffect,
  useState,
  type Dispatch
} from "react";
import { OAUTH_OUTPUT } from "services/graphQL/ciq-queries";
import { ProjectIntegrationsTabSharedContext } from "pages/project-setting-menu/project-integrations/project-integrations-tab-context";
import { Modal } from "antd";
import { IntegrationAuthProcessStatuses } from "../constants";
import { useCustomOAuthPopup } from "./common";
import { useCIQQuerySubscription } from "./ciq-gql-hooks";

type AuthConfig = {
  id: string;
  auth_url?: string;
  [key: string]: any;
};

type OAuthStatus = {
  status: string;
  [key: string]: any;
};

type OAuthOperations = {
  oauth_status: OAuthStatus;
  [key: string]: any;
};

type AuthData = {
  oauth_operations: OAuthOperations[];
};

const buildAuthPayload = (
  oAuthOperations: OAuthOperations,
  authConfig: AuthConfig | null
) => ({
  ...oAuthOperations,
  ...authConfig
});

const dispatchAuthSuccess = (
  source: "subscription" | "project",
  authPayload: ReturnType<typeof buildAuthPayload>,
  subscriptionDispatch: Dispatch<any>,
  projectDispatch: Dispatch<any>
) => {
  const action = {
    type: "SET_SHARED_STATE",
    payload: { authData: authPayload }
  };

  if (source === "subscription") {
    subscriptionDispatch(action);
  }

  if (source === "project") {
    projectDispatch(action);
  }
};

const handleProcessedAuthResult = (
  source: "subscription" | "project",
  oAuthOperations: OAuthOperations,
  authConfig: AuthConfig | null,
  authPromise: { resolve: (value?: any) => void } | null,
  subscriptionDispatch: Dispatch<any>,
  projectDispatch: Dispatch<any>
) => {
  const authPayload = buildAuthPayload(oAuthOperations, authConfig);
  dispatchAuthSuccess(
    source,
    authPayload,
    subscriptionDispatch,
    projectDispatch
  );
  authPromise?.resolve(authPayload);
};

const handleFailedAuthResult = (
  oAuthOperations: OAuthOperations,
  authPromise: { resolve: (value?: any) => void } | null,
  closePopup: () => void
) => {
  authPromise?.resolve(null);
  closePopup();
  Modal.error({
    title: "Authentication failed",
    content: oAuthOperations.output?.error || "Failed to authorize"
  });
};

export const useIntegrationAuthProcess = (
  source: "subscription" | "project" = "subscription",
  gqlClient?: any
) => {
  const { dispatch: subscriptionDispatch } = useContext(
    IntegrationsTabSharedContext
  );

  const { dispatch: projectDispatch } = useContext(
    ProjectIntegrationsTabSharedContext
  );

  const { openPopup, closePopup, isPopupOpen } = useCustomOAuthPopup();

  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [authPromise, setAuthPromise] = useState<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  } | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const { data: authData } = useCIQQuerySubscription<AuthData>(OAUTH_OUTPUT, {
    variables: {
      id: authConfig?.id
    },
    client: gqlClient,
    skip: !authConfig?.id
  });

  const handleAuthDataUpdate = useCallback(() => {
    if (!authData) {
      return;
    }

    const oAuthOperations = authData.oauth_operations[0];
    const authStatus = oAuthOperations?.oauth_status?.status;

    if (!isAuthenticating || !authStatus) {
      return;
    }

    closePopup();

    const isProcessed =
      (authStatus as string) ===
      (IntegrationAuthProcessStatuses.PROCESSED as string);

    if (isProcessed) {
      handleProcessedAuthResult(
        source,
        oAuthOperations,
        authConfig,
        authPromise,
        subscriptionDispatch,
        projectDispatch
      );
    } else {
      handleFailedAuthResult(oAuthOperations, authPromise, closePopup);
    }

    setAuthPromise(null);
    setIsAuthenticating(false);
  }, [
    authData,
    isAuthenticating,
    closePopup,
    source,
    authPromise,
    subscriptionDispatch,
    authConfig,
    projectDispatch
  ]);

  useEffect(() => {
    handleAuthDataUpdate();
  }, [authData, handleAuthDataUpdate]);

  const resetAuthProcess = useCallback(() => {
    setAuthConfig(null);
    setAuthPromise(null);
    setIsAuthenticating(false);
    closePopup();
  }, [closePopup]);

  const initAuthProcess = useCallback(
    (config: AuthConfig) => {
      resetAuthProcess();
      setAuthConfig(config);
      if (config.auth_url) {
        openPopup({ title: "Login to authenticate", url: config.auth_url });
      }
      setIsAuthenticating(true);

      return new Promise((resolve, reject) => {
        setAuthPromise({ resolve, reject });
      });
    },
    [openPopup, resetAuthProcess]
  );

  return {
    initAuthProcess,
    authData: authData?.oauth_operations[0],
    closePopup,
    isPopupOpen,
    resetAuthProcess
  };
};
